import { Button, ButtonLink, Icon, Text } from '@pm/ui';
import * as Sentry from '@sentry/react';
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

type AppErrorPageProps = {
  header?: ReactNode;
  footer?: ReactNode;
  contactSupportLink?: string;
};

const AppErrorPage = ({
  header,
  footer,
  contactSupportLink,
}: AppErrorPageProps) => {
  const { t } = useTranslation('global');

  return (
    <div className="flex min-h-screen flex-col">
      {header}
      <div className="flex flex-1 items-start justify-center sm:items-center bg-radial-gradient motion-safe:animate-gradient bg-[length:400%_400%] px-l py-xxl">
        <div className="w-full max-w-[624px] rounded-2xl bg-white/80 shadow-xl backdrop-blur-xs sm:p-10 p-xxxl text-center space-y-xxxl">
          <div className="space-y-l">
            <Icon
              name="heart_broken"
              size="xxxl"
              color="text-brand-primary-500"
            />
            <Text size="headingL">{t('Errors.Generic.Title')}</Text>
            <Text size="bodyL">{t('Errors.AppCrash.Description')}</Text>
          </div>
          <div className="flex flex-col gap-m">
            <Button
              intent="primary"
              width="full"
              onClick={() => window.location.reload()}
            >
              {t('Errors.AppCrash.Reload')}
            </Button>
            {contactSupportLink && (
              <ButtonLink
                to={contactSupportLink}
                external
                intent="ghost"
                width="full"
              >
                {t('Errors.AppCrash.ContactSupport')}
              </ButtonLink>
            )}
          </div>
          {footer}
        </div>
      </div>
    </div>
  );
};

type AppErrorBoundaryProps = {
  children: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  contactSupportLink?: string;
};
type AppErrorBoundaryState = { crashed: boolean };

// requires brand-specific extension components in
// the case of Freddie due to the geo split between
// US and CA
export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state = { crashed: false };

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, {
      mechanism: { handled: false },
      data: { componentStack: info.componentStack },
    });
  }

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { crashed: true };
  }

  render() {
    if (this.state.crashed) {
      return (
        <AppErrorPage
          header={this.props.header}
          footer={this.props.footer}
          contactSupportLink={this.props.contactSupportLink}
        />
      );
    }
    return this.props.children;
  }
}
